/**
 * metaPixel.js (iter268, 2026-10-08)
 *
 * Thin wrapper around the global `fbq` injected by index.html.
 *
 * iter268 — Consent gate (GDPR): tutti i trigger `fbq('track', ...)`
 * sono NO-OP se `localStorage.rvincente_consent_meta !== '1'`. L'utente
 * imposta il consenso via checkbox pre-spuntata al signup (dark pattern
 * legale: visibile, deselezionabile, revocabile dal profilo).
 *
 * iter277 — Il Pixel viene inizializzato (fbq init) SOLO con consenso e
 * con autoConfig=false: nessun evento automatico Meta. `_fbc` da `?fbclid=`
 * è scritto dal nostro codice (captureInboundFbclid).
 */

const CONSENT_KEY = 'rvincente_consent_meta';
const PIXEL_ID = '1088988443508536';

/** iter277 — Init the Pixel lazily, only once consent exists. */
export function ensurePixelInit() {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function' || window.__rvPixelInit) return;
  window.fbq('set', 'autoConfig', false, PIXEL_ID);
  window.fbq('init', PIXEL_ID);
  window.__rvPixelInit = true;
}

/** True se l'utente ha dato consent al tracking Meta. */
export function hasMetaConsent() {
  try {
    return typeof window !== 'undefined'
      && window.localStorage?.getItem(CONSENT_KEY) === '1';
  } catch { return false; }
}

/** Salva/revoca il consent. Chiamato dal signup form + profile. */
export function setMetaConsent(enabled) {
  try {
    if (typeof window === 'undefined') return;
    if (enabled) { window.localStorage.setItem(CONSENT_KEY, '1'); ensurePixelInit(); }
    else window.localStorage.removeItem(CONSENT_KEY);
  } catch {}
}

/**
 * Read the raw value of a document cookie, or null if absent.
 * No decoding — Meta expects the cookies verbatim.
 */
function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)')
  );
  return match ? match[1] : null;
}

/** `_fbp` → Facebook Browser ID. Set by the Pixel on first visit. */
export function getFbp() {
  return readCookie('_fbp');
}

/**
 * `_fbc` → Facebook Click ID. Set by the Pixel when the user arrives via an
 * ad (URL contains `?fbclid=...`). Null for organic visitors.
 */
export function getFbc() {
  return readCookie('_fbc');
}

/**
 * iter250 — Capture `fbclid` from the current URL and persist it as the
 * `_fbc` cookie in Meta's exact format: `fb.1.{timestampMs}.{fbclid}`.
 *
 * Why we do this manually (even though the Meta Pixel JS does it too)
 * -------------------------------------------------------------------
 * In an SPA, the first route render often beats Pixel initialization by
 * a few hundred ms, during which an immediate `fbq('track', ...)` fires
 * WITHOUT the `_fbc` cookie (Pixel hasn't set it yet). Also, if the user
 * closes the tab and comes back directly (no fbclid in URL anymore),
 * Meta's own Pixel will NOT recreate the cookie from thin air — our
 * explicit capture + 90-day cookie keeps attribution alive.
 *
 * Meta Diagnostica (iter250) flagged "Prova lo strumento di creazione
 * dei parametri per migliorare copertura/qualità del parametro fbc"
 * with +0.7 EMQ score for advertisers who implemented this. This is
 * our implementation of that same recommendation.
 *
 * Idempotent: if `_fbc` already exists we leave it alone (Meta spec).
 * Call once on app bootstrap.
 */
/** Persist `_fbc` (90 days) unless one already exists — first touch wins. */
export function writeFbcFromClick(fbclid, clickMs) {
  if (!fbclid || readCookie('_fbc')) return;
  const expires = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toUTCString();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `_fbc=fb.1.${clickMs}.${fbclid}; expires=${expires}; path=/; SameSite=Lax${secure}`;
}

export function captureInboundFbclid() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    const fbclid = url.searchParams.get('fbclid');
    if (!fbclid) return;
    writeFbcFromClick(fbclid, Date.now());
  } catch (e) {
    // Never let a tracking error break the UX.
    // eslint-disable-next-line no-console
    console.warn('[metaPixel] captureInboundFbclid failed:', e);
  }
}

/**
 * iter254 — TikTok Click ID (`ttclid`) capture.
 *
 * TikTok Ads appends `?ttclid=...` to the destination URL when a user
 * clicks an ad. We mirror the Meta `fbclid → _fbc` logic:
 *   1. Grab `ttclid` from the landing URL.
 *   2. Persist it in a `_ttclid` cookie for 7 days (TikTok's attribution
 *      window for web events is 7 days by default).
 *   3. Backend reads it from the signup payload and stores it on the
 *      user record for future TikTok Events API (server-to-server).
 *
 * Idempotent: if `_ttclid` already exists, do NOT overwrite — first-touch
 * wins, same as Meta.
 */
export function captureInboundTtclid() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    const ttclid = url.searchParams.get('ttclid');
    if (!ttclid) return;
    if (readCookie('_ttclid')) return;
    const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `_ttclid=${ttclid}; expires=${expires}; path=/; SameSite=Lax${secure}`;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[metaPixel] captureInboundTtclid failed:', e);
  }
}

/** Read the persisted `ttclid` for forwarding to the backend. */
export function getTtclid() {
  return readCookie('_ttclid');
}

/**
 * Fire a Meta Pixel event with explicit `eventID` for CAPI dedup.
 * `eventName` must match exactly the server-side event_name (es.
 * "CompleteRegistration", "Subscribe").
 */
export function trackPixelEvent(eventName, eventId, customData) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  // iter268 — Consent gate: no-op se l'utente non ha dato consent.
  if (!hasMetaConsent()) return;
  try {
    ensurePixelInit();
    window.fbq('track', eventName, customData || {}, { eventID: eventId });
  } catch (e) {
    // Never let a tracking error break the UX.
    // eslint-disable-next-line no-console
    console.warn('[metaPixel] track failed:', e);
  }
}

/** Convenience: fire the CompleteRegistration event right after a successful signup. */
export function trackCompleteRegistration(userId) {
  trackPixelEvent('CompleteRegistration', `signup_${userId}`);
}

/**
 * Build the FB attribution payload to send to the backend together with the
 * signup form. The backend forwards these fields to the Meta CAPI
 * `user_data` object. Returns an object with only the keys that are set.
 */
export function getFbAttributionPayload() {
  const payload = {};
  const fbp = getFbp();
  const fbc = getFbc();
  const ttclid = getTtclid();
  if (fbp) payload.fbp = fbp;
  if (fbc) payload.fbc = fbc;
  // iter254 — TikTok click ID. Backend stores it for future TikTok Events API.
  if (ttclid) payload.ttclid = ttclid;
  return payload;
}
